import styled, { css } from 'styled-components';

import { rem } from '../../libs';
import { fontSizes, fontWeights } from '../../theme';
import { ValueOf } from '../../types';
import { subtractUnit } from '../../utils/styles';

import ButtonPrimitive from './ButtonPrimitive';
import { ButtonVariableNames } from './constants';
import { ButtonSize, ButtonThemeVariantValues, ButtonVariant } from './types';

interface ButtonElementProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $isUnderlined?: boolean;
  $iconPosition?: 'left' | 'right';
}

const borderWidthPxByVariant: Record<ButtonVariant, number> = {
  primary: 0,
  secondary: 0,
  'outlined-primary': 2,
  'outlined-secondary': 1,
  destructive: 0,
  text: 0,
};

const variableStyles = css<ButtonElementProps>(
  ({
    theme,
    $size,
    $variant,
    $isUnderlined,
  }): Record<ValueOf<typeof ButtonVariableNames>, string | undefined> => {
    const variant: ButtonThemeVariantValues =
      theme.components.Button[$size][
        $isUnderlined && $variant === 'text' ? 'underline-text' : $variant
      ];

    const borderWidthNumber = borderWidthPxByVariant[$variant];

    const verticalPadding = subtractUnit(
      variant['vertical-padding'],
      borderWidthNumber,
    );

    const horizontalPadding = subtractUnit(
      variant['horizontal-padding'],
      borderWidthNumber,
    );

    return {
      [ButtonVariableNames.Color]: variant['fg-default'] || 'initial',
      [ButtonVariableNames.IconColor]:
        variant['icon-default'] || 'currentColor',
      [ButtonVariableNames.BackgroundColor]: variant['bg-default'] || 'initial',
      [ButtonVariableNames.BorderColor]:
        variant['border-default'] || 'transparent',
      [ButtonVariableNames.BorderWidth]: rem(borderWidthNumber),
      [ButtonVariableNames.HoverColor]: variant['fg-hover'],
      [ButtonVariableNames.HoverIconColor]: variant['icon-hover'],
      [ButtonVariableNames.HoverBackgroundColor]: variant['bg-hover'],
      [ButtonVariableNames.HoverBorderColor]: variant['border-hover'],
      [ButtonVariableNames.FocusOutlineColor]: variant.shadow || 'transparent',
      [ButtonVariableNames.ExpandedBackgroundColor]: variant['bg-focus'],
      [ButtonVariableNames.ExpandedOutlineColor]: variant.shadow,
      [ButtonVariableNames.DisabledColor]: variant['fg-disabled'],
      [ButtonVariableNames.DisabledIconColor]: variant['icon-disabled'],
      [ButtonVariableNames.DisabledBackgroundColor]: variant['bg-disabled'],
      [ButtonVariableNames.Padding]: [verticalPadding, horizontalPadding]
        .filter(Boolean)
        .join(' '),
      [ButtonVariableNames.Gap]: variant.inside,
      [ButtonVariableNames.Radius]: variant.radius,
      [ButtonVariableNames.FontWeight]: `${
        $variant === 'outlined-secondary'
          ? fontWeights.regular
          : fontWeights.medium
      }`,
      [ButtonVariableNames.FontSize]: `${
        $size === 'big' ? fontSizes.l : fontSizes.m
      }`,
      [ButtonVariableNames.IconSize]: `${
        $size === 'big' ? theme.core.iconSize.l : theme.core.iconSize.m
      }`,
    };
  },
);

export const ButtonElement = styled(ButtonPrimitive)<ButtonElementProps>`
  ${variableStyles}

  box-sizing: border-box;
  display: flex;
  flex: 0 0 auto;
  flex-direction: ${({ $iconPosition }) =>
    $iconPosition === 'left' ? 'row' : 'row-reverse'};
  align-items: center;
  justify-content: center;
  min-width: ${({ $variant }) => ($variant === 'text' ? 'initial' : rem(124))};
  overflow: hidden;
  white-space: nowrap;
  text-decoration: ${({ $isUnderlined }) => $isUnderlined && 'underline'};
  text-overflow: ellipsis;
  transition: border-color 0.2s ease-in-out;
  user-select: none;

  /** We use same line-height as icon-size so button is same height with and without icon */
  line-height: var(${ButtonVariableNames.IconSize});
  font-size: var(${ButtonVariableNames.FontSize});
  font-weight: var(${ButtonVariableNames.FontWeight});
  border-radius: var(${ButtonVariableNames.Radius});
  gap: var(${ButtonVariableNames.Gap});
  padding: var(${ButtonVariableNames.Padding});
  color: var(${ButtonVariableNames.Color});
  background-color: var(${ButtonVariableNames.BackgroundColor});
  border: var(${ButtonVariableNames.BorderWidth}) solid
    var(${ButtonVariableNames.BorderColor});

  svg {
    width: var(${ButtonVariableNames.IconSize});
    height: var(${ButtonVariableNames.IconSize});
    transition: fill 0.1s linear;
    fill: var(${ButtonVariableNames.IconColor});
  }

  &:hover {
    background-color: var(
      ${ButtonVariableNames.HoverBackgroundColor},
      var(${ButtonVariableNames.BackgroundColor})
    );
    color: var(
      ${ButtonVariableNames.HoverColor},
      var(${ButtonVariableNames.Color})
    );
    border-color: var(
      ${ButtonVariableNames.HoverBorderColor},
      var(${ButtonVariableNames.BorderColor})
    );

    svg {
      fill: var(
        ${ButtonVariableNames.HoverIconColor},
        var(${ButtonVariableNames.IconColor})
      );
    }
  }

  &:focus-visible {
    outline: 4px solid var(${ButtonVariableNames.FocusOutlineColor});
  }

  &[aria-expanded='true'] {
    outline: 4px solid
      var(
        ${ButtonVariableNames.ExpandedOutlineColor},
        var(${ButtonVariableNames.FocusOutlineColor})
      );

    background-color: var(
      ${ButtonVariableNames.ExpandedBackgroundColor},
      var(${ButtonVariableNames.BackgroundColor})
    );
  }

  &:disabled {
    background-color: var(${ButtonVariableNames.DisabledBackgroundColor});
    cursor: not-allowed;
    color: var(${ButtonVariableNames.DisabledColor});

    svg {
      fill: var(
        ${ButtonVariableNames.DisabledIconColor},
        var(${ButtonVariableNames.IconColor})
      );
    }
  }
`;
